import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface PricingModel {
  id: string
  name: string
  description: string
  metric: string
  unit: string
  tiers: { min: number; max: number; price_per_unit: number; description: string }[]
  billing_period: string
  active: boolean
}

interface CustomerUsage {
  id: string
  customer_id: string
  customer_name: string
  billing_period: string
  usage: Record<string, number>
  calculated_cost: Record<string, number>
  total_usage_cost: number
  base_plan_cost: number
  total_cost: number
}

export default function UsagePricingPage() {
  const [models, setModels] = useState<PricingModel[]>([])
  const [customerUsage, setCustomerUsage] = useState<CustomerUsage[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedModel, setSelectedModel] = useState<PricingModel | null>(null)
  const [calcMetric, setCalcMetric] = useState('')
  const [calcQuantity, setCalcQuantity] = useState('')
  const [calcResult, setCalcResult] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [modRes, usageRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/usage-pricing/models'),
      fetch(API_BASE + '/usage-pricing/customer-usage'),
      fetch(API_BASE + '/usage-pricing/statistics')
    ])
    if (modRes.ok) {
      const modelData = (await modRes.json()).pricing_models || []
      setModels(modelData)
      if (modelData.length > 0) setCalcMetric(modelData[0].metric)
    }
    if (usageRes.ok) setCustomerUsage((await usageRes.json()).customer_usage || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const calculateCost = async () => {
    if (!calcMetric || !calcQuantity) return
    const res = await fetch(`${API_BASE}/usage-pricing/calculate?metric=${calcMetric}&quantity=${calcQuantity}`)
    if (res.ok) setCalcResult(await res.json())
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`
    return num.toString()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Usage-based Pricing Calculator</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pricing Models</div>
              <div className="text-2xl font-bold">{statistics.total_pricing_models}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Customers</div>
              <div className="text-2xl font-bold">{statistics.customers_with_usage}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Revenue</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue_this_month)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Usage Revenue</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.usage_based_revenue)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Base Revenue</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.base_plan_revenue)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Usage %</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.usage_revenue_percentage}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Pricing Models</h2></div>
              <div className="divide-y">
                {models.map(model => (
                  <div key={model.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedModel(model)}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">{model.name}</div>
                        <div className="text-sm text-gray-500">{model.description}</div>
                      </div>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{model.unit}</span>
                    </div>
                    <div className="flex gap-2">
                      {model.tiers.map((tier, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {tier.description}: ${tier.price_per_unit}/{model.unit}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Customer Usage This Month</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">API Requests</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AI Queries</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {customerUsage.map(cu => (
                    <tr key={cu.id}>
                      <td className="px-6 py-4 font-medium">{cu.customer_name}</td>
                      <td className="px-6 py-4">{formatNumber(cu.usage.api_requests)}</td>
                      <td className="px-6 py-4">{cu.usage.storage_gb} GB</td>
                      <td className="px-6 py-4">{formatNumber(cu.usage.ai_queries)}</td>
                      <td className="px-6 py-4 text-blue-600">{formatCurrency(cu.total_usage_cost)}</td>
                      <td className="px-6 py-4 font-semibold">{formatCurrency(cu.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Cost Calculator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Metric</label>
                  <select value={calcMetric} onChange={e => setCalcMetric(e.target.value)} className="w-full p-2 border rounded">
                    {models.map(m => (
                      <option key={m.id} value={m.metric}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input type="number" value={calcQuantity} onChange={e => setCalcQuantity(e.target.value)} placeholder="Enter quantity" className="w-full p-2 border rounded" />
                </div>
                <button onClick={calculateCost} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Calculate</button>
                {calcResult && (
                  <div className="p-4 bg-gray-50 rounded">
                    <div className="text-2xl font-bold text-green-600 mb-2">{formatCurrency(calcResult.total_cost)}</div>
                    <div className="text-sm text-gray-500 mb-2">for {formatNumber(calcResult.quantity)} {calcResult.metric}</div>
                    <div className="space-y-1">
                      {calcResult.breakdown?.map((b: any, i: number) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span>{b.tier}: {formatNumber(b.units)}</span>
                          <span>{formatCurrency(b.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedModel && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedModel.name} Tiers</h3>
                <div className="space-y-3">
                  {selectedModel.tiers.map((tier, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">{tier.description}</span>
                        <span className="text-green-600 font-semibold">${tier.price_per_unit}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {tier.max === -1 ? `${formatNumber(tier.min)}+` : `${formatNumber(tier.min)} - ${formatNumber(tier.max)}`} {selectedModel.unit}s
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
